import Core from 'smooothy'
import gsap from 'gsap'
import { declutterLabels } from './map-labels'

/**
 * Push overlapping map labels apart vertically.
 *
 * Labels are grouped per map (via [data-day-map]) and then per side
 * (via [data-side]), since a left-pointing and right-pointing label never
 * actually collide. Within each group we sort top-to-bottom and shift any
 * label that would overlap the previous one. A manual base offset can be set
 * per label with data-offset-y and it will be preserved.
 *
 * Safe to call repeatedly: it re-establishes each label's base position
 * before measuring, so it's idempotent and correct after resizes.
 */
class LinkSlider extends Core {
	constructor(container, config = {}) {
		super(container.querySelector('[data-slider]'), {
			infinite: true,
			snap: true,
			variableWidth: false,
			...config,
		})
		this.parallaxElements = [...this.wrapper.querySelectorAll('[data-p]')]
		this.boundUpdate = this.update.bind(this)
		gsap.ticker.add(this.boundUpdate)
		this.#handleLinks()
	}

	#handleLinks() {
		;[...this.wrapper.querySelectorAll('a')].forEach(item => {
			let startX = 0
			let startY = 0
			let startTime = 0
			let isDragging = false

			item.style.pointerEvents = 'none'

			const handleMouseDown = e => {
				startX = e.clientX
				startY = e.clientY
				startTime = Date.now()
				isDragging = false
			}

			const handleMouseMove = e => {
				if (!startTime) return
				const deltaX = Math.abs(e.clientX - startX)
				const deltaY = Math.abs(e.clientY - startY)
				if (deltaX > 5 || deltaY > 5) isDragging = true
			}

			const handleMouseUp = () => {
				const deltaTime = Date.now() - startTime
				if (!isDragging && deltaTime < 200) item.click()
				startTime = 0
				isDragging = false
			}

			item.parentElement.addEventListener('mousedown', handleMouseDown)
			item.parentElement.addEventListener('mousemove', handleMouseMove)
			item.parentElement.addEventListener('mouseup', handleMouseUp)
		})
	}
}

export default class StationCarousel {
	constructor() {
		this.carousels = document.querySelectorAll('[data-station-carousel]')
		this.#init()
	}

	#init() {
		this.carousels.forEach(carousel => this.#setupCarousel(carousel))
	}

	#setupCarousel(carousel) {
		const wrapper = carousel.querySelector('[data-slider]')
		if (!wrapper) return

		const isFade = carousel.hasAttribute('data-fade-slider')
		if (isFade) {
			this.#setupFadeCarousel(carousel, wrapper)
			return
		}

		// Largest value getSlidesPerView() returns. Clones are sized for this so the
		// loop stays gapless at every breakpoint without rebuilding on resize.
		const MAX_SLIDES_PER_VIEW = 4
		const CLONE_ATTR = 'data-clone'

		const allItems = [...wrapper.children]
		const filterButtons = carousel.querySelectorAll('[data-term-slug]')
		const prevButton = carousel.querySelector('[data-prev]')
		const nextButton = carousel.querySelector('[data-next]')
		const progressEl = carousel.querySelector('[data-progress]')

		const onUpdate = core => {
			if (progressEl) {
				const pct = (core.progress || 0) * 100
				progressEl.style.width = `${pct}%`
			}
		}

		const getSlidesPerView = () => {
			const w = window.innerWidth
			if (w >= 1280) return 4
			if (w >= 1024) return 3
			if (w >= 640) return 2
			return 1
		}

		const removeClones = () => {
			wrapper.querySelectorAll(`[${CLONE_ATTR}]`).forEach(clone => clone.remove())
		}

		// Duplicate the real slides until the track holds at least 2x a full view,
		// so there's always a spare viewport to fill the wrap-around edge.
		const addClones = () => {
			const reals = [...wrapper.children]
			const count = reals.length
			if (!count) return
			const target = MAX_SLIDES_PER_VIEW * 2
			if (count >= target) return
			const sets = Math.ceil(target / count) // total copies of the set, incl. the original
			for (let s = 1; s < sets; s++) {
				reals.forEach(item => {
					const clone = item.cloneNode(true)
					clone.setAttribute(CLONE_ATTR, '')
					clone.setAttribute('aria-hidden', 'true')
					clone.querySelectorAll('[id]').forEach(el => el.removeAttribute('id')) // avoid dup IDs (svg masks etc.)
					clone.querySelectorAll('a').forEach(a => a.setAttribute('tabindex', '-1'))
					wrapper.appendChild(clone)
				})
			}
		}

		const buildSlider = () => {
			removeClones()
			const spv = getSlidesPerView()
			const count = wrapper.children.length // reals only — clones just removed
			const canSwipe = count > spv
			const canLoop = count >= spv
			if (canLoop) addClones()
			return new LinkSlider(carousel, {
				onUpdate,
				infinite: canLoop,
				dragEnabled: canSwipe || canLoop,
			})
		}

		let slider = buildSlider()

		const rebuild = () => {
			gsap.ticker.remove(slider.boundUpdate)
			slider.destroy()
			slider = buildSlider()
			declutterLabels(carousel)
		}

		const handlePrev = () => slider.goToPrev()
		const handleNext = () => slider.goToNext()
		prevButton?.addEventListener('click', handlePrev)
		nextButton?.addEventListener('click', handleNext)

		const filter = slug => {
			removeClones() // drop stale clones before reconciling the real set
			allItems.forEach(item => {
				const link = item.querySelector('[data-region]') || item
				const regions = (link.dataset.region || '').split(' ').filter(Boolean)
				const matches = slug === 'all' || regions.includes(slug)
				if (matches) {
					if (item.parentElement !== wrapper) wrapper.appendChild(item)
				} else if (item.parentElement === wrapper) {
					item.remove()
				}
				item.style.transform = ''
			})
			rebuild()
		}

		filterButtons.forEach(button => {
			button.addEventListener('click', () => {
				filterButtons.forEach(b => b.classList.remove('active'))
				button.classList.add('active')
				filter(button.dataset.termSlug)
			})
		})

		// Recompute loop / drag / clone state when crossing breakpoints.
		let resizeTimer
		window.addEventListener('resize', () => {
			clearTimeout(resizeTimer)
			resizeTimer = setTimeout(rebuild, 200)
		})
	}

	#setupFadeCarousel(carousel, wrapper) {
		const slides = [...wrapper.children]
		const prevButtons = carousel.querySelectorAll('[data-prev]')
		const nextButtons = carousel.querySelectorAll('[data-next]')
		const dots = carousel.querySelectorAll('[data-goto]')
		let current = 0

		wrapper.style.display = 'grid'
		wrapper.style.touchAction = 'pan-y'
		slides.forEach((slide, i) => {
			Object.assign(slide.style, {
				gridArea: '1 / 1',
				width: '100%',
				opacity: i === 0 ? '1' : '0',
				pointerEvents: i === 0 ? 'auto' : 'none',
				transition: 'opacity 0.5s ease',
				paddingRight: '0',
			})
		})

		const syncDots = () => {
			dots.forEach(d => {
				;+d.dataset.goto === current ? d.setAttribute('data-active', '') : d.removeAttribute('data-active')
			})
		}
		syncDots()

		const goTo = index => {
			const prev = current
			current = (index + slides.length) % slides.length
			if (prev === current) return
			slides[prev].style.opacity = '0'
			slides[prev].style.pointerEvents = 'none'
			slides[current].style.opacity = '1'
			slides[current].style.pointerEvents = 'auto'
			syncDots()

			// Re-space labels for the slide coming into view.
			declutterLabels(slides[current])
		}

		prevButtons.forEach(b => b.addEventListener('click', () => goTo(current - 1)))
		nextButtons.forEach(b => b.addEventListener('click', () => goTo(current + 1)))
		dots.forEach(d => d.addEventListener('click', () => goTo(+d.dataset.goto)))

		// Swipe / drag
		let startX = 0
		let startY = 0
		let tracking = false
		let axisLocked = false
		let horizontal = false

		const onStart = (x, y) => {
			startX = x
			startY = y
			tracking = true
			axisLocked = false
			horizontal = false
		}

		const onMove = (x, y, e) => {
			if (!tracking) return
			const dx = x - startX
			const dy = y - startY
			if (!axisLocked && (Math.abs(dx) > 8 || Math.abs(dy) > 8)) {
				axisLocked = true
				horizontal = Math.abs(dx) > Math.abs(dy)
			}
			if (horizontal && e && e.cancelable) e.preventDefault()
		}

		const onEnd = x => {
			if (!tracking) return
			tracking = false
			const dx = x - startX
			if (horizontal && Math.abs(dx) > 40) {
				dx < 0 ? goTo(current + 1) : goTo(current - 1)
			}
		}

		wrapper.addEventListener('touchstart', e => onStart(e.touches[0].clientX, e.touches[0].clientY), { passive: true })
		wrapper.addEventListener('touchmove', e => onMove(e.touches[0].clientX, e.touches[0].clientY, e), {
			passive: false,
		})
		wrapper.addEventListener('touchend', e => onEnd(e.changedTouches[0].clientX))

		wrapper.addEventListener('mousedown', e => {
			onStart(e.clientX, e.clientY)
			e.preventDefault()
		})
		window.addEventListener('mousemove', e => {
			if (tracking) onMove(e.clientX, e.clientY, e)
		})
		window.addEventListener('mouseup', e => {
			if (tracking) onEnd(e.clientX)
		})
	}
}
